(() => {
  if (window.CMAQuickEconomics) return;
  function finiteInput(value) {
    if (value === '' || value === null || value === undefined) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function calculate(rows) {
    const channels = rows.map((row) => {
      const price = finiteInput(row.price);
      const cost = finiteInput(row.cost);
      const target = finiteInput(row.target);
      const volume = finiteInput(row.volume);
      const valid = price !== null && price > 0 && price <= 1e9 && volume !== null && volume >= 0 && volume <= 1e9 && Number.isSafeInteger(volume) && Number.isFinite(price * volume);
      const costValid = cost !== null && cost >= 0 && cost <= 1e9 && (volume === null || Number.isFinite(cost * volume));
      const targetValid = target !== null && target >= 0 && target < 100;
      return {
        type: row.type,
        price, cost, target, volume, valid, costValid, targetValid,
        revenue: valid ? price * volume : null,
        contribution: valid && costValid ? (price - cost) * volume : null,
        margin: valid && costValid ? ((price - cost) / price) * 100 : null,
        allowableCost: valid && targetValid ? price * (1 - target / 100) : null,
      };
    });
    const complete = channels.length > 0 && channels.every((row) => row.valid);
    const revenue = complete ? channels.reduce((sum, row) => sum + row.revenue, 0) : null;
    const contribution = complete && channels.every((row) => row.costValid)
      ? channels.reduce((sum, row) => sum + row.contribution, 0)
      : null;
    return { channels, complete, revenue, contribution };
  }

  function describeConfiguration(design, features) {
    const parts = features.filter(f => f.label !== 'Illustrated finish').map(({label, value}) => {
      const v = value.trim(), lower = v.toLowerCase();
      if (label === 'Finish') return lower + ' finish';
      if (label === 'Power') return /rechargeable/i.test(v) ? 'rechargeable battery' : lower + ' power';
      if (label === 'Environment') return lower === 'outdoor' ? 'outdoor rated' : lower === 'wls' ? 'Wet Light Systems outdoor technology' : lower;
      if (label === 'Max. Output') return /[0-9]/.test(v) ? 'up to ' + v.replace(/[^0-9.]/g, '') + ' lumens' : 'light output to explore';
      if (label === 'Light') return lower === 'warm dim' ? 'warm dimming' : lower;
      if (label === 'Control') return lower === 'smooth' ? 'smooth dimming' : lower === 'step' ? 'step dimming' : lower;
      if (label === 'Optic Type') return lower + ' optic';
      if (label === 'Shade Type') return lower === 'none' ? 'no shade' : lower + ' shade';
      if (label === 'Lamp Type') return lower + ' lamp';
      if (label === 'Integrated Type') return v;
      return lower;
    });
    if (!parts.length) return design + '.';
    const list = parts.length < 2 ? parts[0] : parts.slice(0, -1).join(', ') + (parts.length > 2 ? ', and ' : ' and ') + parts.at(-1);
    return design + ' with ' + list + '.';
  }

  function init(root) {
    if (root.dataset.cmaExplorerReady) return;
    root.dataset.cmaExplorerReady = 'true';
    const calculator = root.querySelector('[data-cma-calculator]');
    const container = root.querySelector('[data-channels]');
    const template = root.querySelector('[data-channel-template]');
    const currencyInput = root.querySelector('[data-currency]');
    const form = root.querySelector('form');
    const body = root.querySelector('[data-scenario-body]');
    const preview = root.querySelector('[data-share-preview]');
    let latest = null, snapshotKey = '', frozenSnapshot = null, prepared = null, pending = false;
    const status = root.querySelector('[data-export-status]');
    const delivery = root.querySelector('[data-proposal-delivery]');
    const isProduct = root.dataset.context === 'product';

    function money(amount) {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyInput.value, maximumFractionDigits: 2 }).format(amount);
    }

    function featureSummary() {
      if (!isProduct) return [];
      const chosen = [...root.querySelectorAll('[data-feature-group]')].filter(group => !group.hidden).map(group => {
        const input = group.querySelector('input:checked');
        const story = [...group.querySelectorAll('[data-feature-story]')].find(n => n.dataset.featureStory === input?.value);
        return input ? { label: group.querySelector('legend').textContent.trim(), value: input.value,
          benefit: input.closest('label').querySelector('.cma-explorer__option-copy > span')?.textContent.trim() || '',
          note: story?.querySelector('small')?.textContent.trim() || '' } : null;
      }).filter(Boolean);
      const finish = document.querySelector('input[name^="model-finish-"]:checked');
      if (finish) chosen.unshift({ label: 'Illustrated finish', value: finish.value.replaceAll('-', ' ') });
      return chosen;
    }

    function snapshot() {
      const field = selector => root.querySelector(selector)?.value.trim() || '';
      const source = root.querySelector('[data-proposal-product]');
      const panel = isProduct ? document.querySelector('[data-cma-finish-panel]:not([hidden]) [data-cma-view-panel]:not([hidden])') : null;
      const img = panel?.querySelector('img');
      const data = {
        context: isProduct ? 'product' : 'commissioned', design: root.dataset.design,
        recipient: root.dataset.recipient, currency: currencyInput.value, result: latest,
        configuration: isProduct ? describeConfiguration(root.dataset.design, featureSummary()) : '',
        features: featureSummary(), company: field('[data-proposal-company]'), market: field('[data-proposal-market]'),
        application: field('[data-proposal-application]'), name: field('[name="contact[name]"]'),
        email: field('[name="contact[email]"]'), question: field('[name="contact[question]"]'),
        product: source && isProduct ? { url: source.dataset.url, value: source.dataset.value, description: source.dataset.description,
          image: img?.currentSrc || img?.src || '', caption: panel?.querySelector('figcaption')?.textContent.trim() || img?.alt || '',
          specs: [...document.querySelectorAll('.cma-design-product__specifications tr')].map(row => ({label: row.querySelector('th')?.textContent.trim(),value: row.querySelector('td')?.textContent.trim()})).filter(row => row.label && row.value)
        } : null,
      };
      const key = JSON.stringify(data);
      if (key !== snapshotKey) {
        snapshotKey = key;
        frozenSnapshot = { ...data, id: 'CMA-' + crypto.randomUUID().slice(0, 8).toUpperCase(), date: new Date().toISOString().slice(0, 10), version: '2.0' };
        if (prepared) URL.revokeObjectURL(prepared.url);
        prepared = null; delivery.hidden = true;
      }
      return frozenSnapshot;
    }

    function rows() {
      return [...container.querySelectorAll('[data-channel]')].map((node) => {
        const value = (key) => node.querySelector(`[data-field="${key}"]`).value;
        return { type: value('type'), price: value('price'), cost: value('cost'), target: value('target'), volume: value('volume') };
      });
    }

    function shareText(s) {
      const m = n => n === null ? 'Not calculated' : s.currency + ' ' + n.toLocaleString('en-US', {maximumFractionDigits:2});
      const lines = ['CALL ME ANTHONY', s.context === 'product' ? 'DESIGN LICENSING / PRODUCT OPPORTUNITY BRIEF' : 'UNIT ECONOMICS / PRODUCT OPPORTUNITY BRIEF',
        `Reference: ${s.id} | Prepared: ${s.date}`, `Prepared for: ${s.company || s.name || 'stakeholder review'}`,
        `Market / audience: ${s.market || 'To define together'}`, `Application: ${s.application || 'To define together'}`,
        `Decision to explore: ${s.question || 'Review feasibility and commercial objectives'}`];
      if (s.product) {
        lines.push(`Design: ${s.design}`, s.configuration, `Product page: ${s.product.url}`, s.product.value || s.product.description);
        s.product.specs.forEach(v => lines.push(`${v.label}: ${v.value}`));
      }
      s.features.forEach(v => lines.push(`${v.label}: ${v.value}${v.note ? ' | ' + v.note : ''}`));
      lines.push('CHANNEL OPPORTUNITY', `Currency: ${s.currency} | Annual planning view. All financial inputs are visitor-defined targets.`);
      s.result.channels.forEach((r,i) => lines.push(`Channel ${i+1}: ${r.type}`,
        `Price per unit: ${m(r.price)} | Unit cost target: ${r.costValid?m(r.cost):'Not entered'}`,
        `Margin target: ${r.targetValid?r.target+'%':'Not entered'} | Annual units: ${r.volume === null?'Not entered':r.volume}`,
        `Revenue: ${m(r.revenue)} | Illustrative gross contribution: ${m(r.contribution)}`,
        `Implied margin: ${r.margin===null?'Not calculated':r.margin.toFixed(1)+'%'} | Maximum cost at target margin: ${m(r.allowableCost)}`));
      lines.push(`Combined revenue: ${m(s.result.revenue)}`, `Combined illustrative gross contribution: ${m(s.result.contribution)}`,
        'NEXT STEPS: Align the opportunity; shape the product; validate assumptions; agree the engagement.',
        'Visitor-defined planning targets, not CMA pricing or a binding offer. Contribution is not net profit. Unentered costs and licensing terms are excluded. Configuration and certification require review.');
      return lines.join('\n');
    }

    function render() {
      latest = calculate(rows());
      latest.channels.forEach((row, index) => {
        const node = container.querySelectorAll('[data-channel]')[index];
        const result = node.querySelector('[data-channel-result]');
        if (!row.valid) {
          result.textContent = root.dataset.incomplete;
          return;
        }
        const parts = [`Revenue ${money(row.revenue)}`];
        if (row.contribution !== null) parts.push(`gross contribution ${money(row.contribution)} at your cost target; implied margin ${row.margin.toFixed(1)}%`);
        if (row.allowableCost !== null) parts.push(`maximum unit cost at your target margin ${money(row.allowableCost)}`);
        if (row.allowableCost !== null && row.costValid) parts.push(row.cost <= row.allowableCost ? 'cost target meets margin target' : 'cost target exceeds margin allowance');
        result.textContent = parts.join(' · ');
      });
      root.querySelector('[data-total-revenue]').textContent = latest.revenue === null ? '—' : money(latest.revenue);
      root.querySelector('[data-total-contribution]').textContent = latest.contribution === null ? '—' : money(latest.contribution);
      root.querySelector('[data-result-explain]').textContent = latest.complete
        ? latest.contribution === null
          ? root.dataset.resultPartial
          : root.dataset.resultComplete
        : root.dataset.resultEmpty;
      const current = snapshot();
      root.querySelectorAll('[data-configuration-summary]').forEach(node => node.textContent = current.configuration);
      const text = shareText(current);
      body.value = text;
      preview.textContent = text;

    }

    function addChannel() {
      if (container.querySelectorAll('[data-channel]').length >= 5) return;
      const node = template.content.firstElementChild.cloneNode(true);
      container.append(node);
      node.querySelector('[data-channel-title]').textContent = `${root.dataset.channelLabel} ${container.children.length}`;
      if (container.children.length === 1) node.querySelector('[data-remove-channel]').hidden = true;
      node.querySelector('[data-remove-channel]').addEventListener('click', () => {
        node.remove();
        [...container.children].forEach((child, i) => child.querySelector('[data-channel-title]').textContent = `${root.dataset.channelLabel} ${i + 1}`);
        container.querySelector('[data-remove-channel]').hidden = container.children.length === 1;
        root.querySelector('[data-add-channel]').hidden = container.children.length >= 5;
        render();
      });
      node.addEventListener('input', render);
      node.addEventListener('change', render);
      root.querySelector('[data-add-channel]').hidden = container.children.length >= 5;
      render();
    }

    function syncEmitterGroups() {
      const emitter = root.querySelector('[data-feature-group="emitter-type"] input:checked')?.value;
      root.querySelectorAll('[data-emitter]').forEach((group) => {
        group.hidden = group.dataset.emitter !== emitter;
        group.disabled = group.hidden;
      });
    }

    root.querySelectorAll('[data-feature-group]').forEach((group) => {
      group.addEventListener('change', () => {
        const value = group.querySelector('input:checked')?.value;
        group.querySelectorAll('[data-feature-story]').forEach((story) => story.hidden = story.dataset.featureStory !== value);
        syncEmitterGroups();
        render();
      });
    });
    root.querySelector('[data-add-channel]').addEventListener('click', addChannel);
    function validateExport() {
      for (const node of container.querySelectorAll('input')) {
        if (!node.checkValidity()) { node.reportValidity(); throw new Error('Please correct the highlighted channel input.'); }
      }
    }
    async function prepare() {
      render(); validateExport();
      const s = snapshot();
      if (prepared?.id === s.id) return prepared;
      status.textContent = root.dataset.exportStatus1;
      const blob = await window.CMAProposal.create(s);
      if (snapshot().id !== s.id) throw new Error('Your inputs changed while preparing. Please prepare the updated report again.');
      const filename = `cma-${isProduct ? s.design.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'unit-economics'}-${s.id}.pdf`;
      prepared = { id:s.id, blob, filename, url:URL.createObjectURL(blob), snapshot:s };
      return prepared;
    }
    function download(p) {
      const a = document.createElement('a'); a.href=p.url; a.download=p.filename; document.body.append(a); a.click(); a.remove();
    }
    function mail(p, toAnthony) {
      const s = p.snapshot;
      const title = s.context === 'product' ? `${s.design} design opportunity report` : 'Unit Economics report';
      const text = `Please find my Call Me Anthony report attached for discussion.\n\nReference: ${s.id}\nPrepared for: ${s.company || s.name || 'stakeholder review'}\nAnnual revenue target: ${s.result.revenue===null?'Incomplete':s.currency+' '+s.result.revenue.toLocaleString('en-US')}\nDecision to explore: ${s.question || 'Product feasibility and commercial opportunity'}\n\nBefore sending: attach ${p.filename}.`;
      const href = `mailto:${toAnthony ? s.recipient : ''}?subject=${encodeURIComponent(title+' | '+s.id)}&body=${encodeURIComponent(text)}`;
      const a = root.querySelector('[data-proposal-mail]'); a.href=href;
      const save = root.querySelector('[data-pdf-download]'); save.href=p.url; save.download=p.filename;
      delivery.hidden=false; return a;
    }
    async function action(kind) {
      if (pending) return;
      pending = true;
      try {
        const p = await prepare();
        if (kind === 'save') { download(p); status.textContent = root.dataset.exportStatus2; }
        else if (kind === 'share') {
          const file = new File([p.blob],p.filename,{type:'application/pdf'});
          if (navigator.canShare?.({files:[file]}) && navigator.share) {
            try { await navigator.share({files:[file],title:'Call Me Anthony report | '+p.id}); status.textContent=root.dataset.exportStatus3; return; }
            catch (error) { if (error.name === 'AbortError') { status.textContent=root.dataset.exportStatus4; return; } }
          }
          download(p); mail(p,false); status.textContent=root.dataset.exportStatus5;
        } else {
          download(p); const a=mail(p,true); a.click();
          status.textContent=root.dataset.exportStatus6;
        }
      } catch (error) { status.textContent=error.message || 'Unable to prepare the PDF. Please retry.'; }
      finally { pending=false; }
    }
    function chooseAction(kind) {
      const takeaway = kind === 'save' || kind === 'email';
      root.querySelectorAll('[data-next-action]').forEach(button => button.setAttribute('aria-expanded', String(button.dataset.nextAction === kind)));
      root.querySelectorAll('[data-action-panel]').forEach(panel => panel.hidden = panel.dataset.actionPanel !== (takeaway ? 'takeaway' : 'inquiry'));
      root.querySelector('[data-save-results]').hidden = kind !== 'save';
      root.querySelector('[data-email-results]').hidden = kind !== 'email';
      status.textContent = '';
      delivery.hidden = true;
    }
    root.querySelectorAll('[data-next-action]').forEach(button => button.addEventListener('click', () => chooseAction(button.dataset.nextAction)));
    root.querySelector('[data-save-results]').addEventListener('click', () => action('save'));
    root.querySelector('[data-email-results]').addEventListener('click', () => action('share'));
    root.querySelectorAll('[data-proposal-company],[data-proposal-market],[data-proposal-application],form input,form textarea').forEach(node => node.addEventListener('input', render));
    document.querySelectorAll('[data-cma-view-button],[data-cma-next],[data-cma-previous]').forEach(node => node.addEventListener('click', () => setTimeout(render,0)));
    currencyInput.addEventListener('change', render);
    document.querySelectorAll('input[name^="model-finish-"]').forEach((finish) => finish.addEventListener('change', render));
    form.addEventListener('submit', () => {
      render();
      if (!body.value.trim()) body.value = shareText(snapshot());
    });

    syncEmitterGroups();
    addChannel();
    if (form.querySelector('[data-cma-success],[data-cma-error]')) {
      chooseAction('explore');
      const compact = root.querySelector('.cma-explorer__compact');
      if (compact) compact.open = true;
      form.querySelector('[data-cma-success],[data-cma-error]')?.focus();
    }
  }

  window.CMAQuickEconomics = { calculate, describeConfiguration };
  const start = () => document.querySelectorAll('[data-cma-explorer]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();



